import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import React, { Component, useEffect, useRef, useState } from 'react';
import { SizeMe } from 'react-sizeme';
import settings from '/@/core/settings';
import { isMobile } from '/@/utils';
import styles from './styles.module.css';

interface Module {
    expandable?: boolean;
    icon?: string;
    label?: string;
    component: React.ReactNode;
}

interface ModuleLayoutProps {
    sidebar: React.ReactNode;
    modules: Module[];
    module?: React.ReactNode;
    actions?: React.ReactNode;
    weeds?: boolean;
    sidebarOnTop?: boolean;
    className?: string;
    classNameActions?: string;
    size?: string;
}

const ModuleLayout: React.FC<ModuleLayoutProps> = ({
    sidebar,
    modules,
    module,
    actions,
    weeds = false,
    sidebarOnTop,
    className = '',
    classNameActions,
    size,
}) => {
    const listNodeRef = useRef<HTMLDivElement | null>(null);
    const [expandedModuleIndex, setExpandedModuleIndex] = useState<number | undefined>(0);

    // Set first expandable module
    useEffect(() => {
        if (modules) {
            for (const [i, m] of modules.entries()) {
                if (m.expandable) {
                    setExpandedModuleIndex(i);
                    break;
                }
            }
        }
    }, [modules]);

    const scrollTo = (moduleIndex: number) => {
        if (listNodeRef.current) {
            const moduleElement = listNodeRef.current.children[moduleIndex];
            if (moduleElement) {
                setTimeout(() => moduleElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 500);
            }
        }
    };

    return (
        <div className={classNames(styles.root, sidebarOnTop ? styles.flexCol : undefined)}>
            {sidebar && (
                <div className={classNames(styles.sidebar, sidebarOnTop ? styles.wFull : undefined)}>{sidebar}</div>
            )}
            <div className={styles.main}>
                <div className={classNames(styles.mainInner, className)}>
                    {weeds ? (
                        module
                    ) : (
                        <div className={styles.list} ref={listNodeRef}>
                            {modules.map((module, index) => (
                                <div key={index} className={index === modules.length - 1 ? styles.last : undefined}>
                                    <div className={styles[`theme-${settings.app.theme}`]}>
                                        {React.cloneElement(module.component as React.ReactElement, {
                                            layoutSize: size,
                                            expanded: index === expandedModuleIndex,
                                            expand: () => {
                                                if (expandedModuleIndex === index) setExpandedModuleIndex(undefined);
                                                else setExpandedModuleIndex(index);
                                                scrollTo(index);
                                            },
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {actions && <div className={classNames(classNameActions, styles.actions)}>{actions}</div>}
            </div>
        </div>
    );
};

@autobind
export default class SizeAwareModuleLayout extends Component {
    static contextTypes = {
        manageSize: PropTypes.bool,
    };

    render() {
        if (!this.props.manageSize) return <ModuleLayout {...this.props} />;

        // Note: SizeMe doesn't calculate height correctly in mobile / tablet.
        return (
            <SizeMe
                monitorWidth
                monitorHeight={!isMobile()}
                render={(sizeProps) => <ModuleLayout {...sizeProps} {...this.props} />}
            />
        );
    }
}
